// Generates a Tailwind-style 50-950 tonal ramp from a single seed hex color,
// so the admin only has to pick one color per role and every existing
// bg-purple-500 / text-violet-700 / etc. still gets a sensible shade.

export const DEFAULT_THEME = {
  primaryColor: "#7c3aed",
  secondaryColor: "#4f46e5",
  accentColor: "#B99CE2",
  backgroundColor: "#ffffff",
};

const RAMP_LIGHTNESS = {
  50: 97,
  100: 94,
  200: 88,
  300: 79,
  400: 68,
  500: 58,
  600: 49,
  700: 41,
  800: 33,
  900: 26,
  950: 16,
};

function hexToHsl(hex) {
  const clean = hex.replace("#", "");
  const full = clean.length === 3 ? clean.split("").map((c) => c + c).join("") : clean;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const delta = max - min;
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    switch (max) {
      case r: h = ((g - b) / delta + (g < b ? 6 : 0)) * 60; break;
      case g: h = ((b - r) / delta + 2) * 60; break;
      default: h = ((r - g) / delta + 4) * 60; break;
    }
  }

  return { h, s: s * 100, l: l * 100 };
}

function hslToHex(h, s, l) {
  const sat = s / 100;
  const light = l / 100;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = light - c / 2;

  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Shade 600 is EXACTLY the colour the admin picked; lighter shades (50–500)
// blend from it towards white and darker ones (700–950) towards black, using
// the same spacing as RAMP_LIGHTNESS. (Before, only the hue was kept, so e.g.
// #0284c7 came out as #0ca1ed and two blues of the same hue looked identical.)
export function generateRamp(seedHex) {
  const seed = String(seedHex || "").trim();
  const { h, s, l } = hexToHsl(seed);
  const base = RAMP_LIGHTNESS[600];
  const lightest = RAMP_LIGHTNESS[50];
  const darkest = RAMP_LIGHTNESS[950];
  const ramp = {};
  Object.entries(RAMP_LIGHTNESS).forEach(([step, stepL]) => {
    if (Number(step) === 600) { ramp[step] = (seed.startsWith("#") ? seed : `#${seed}`).toLowerCase(); return; }
    let light;
    if (stepL > base) {
      const f = (stepL - base) / (lightest - base);           // 0 at 600 → 1 at 50
      light = l + (Math.max(lightest, l + 1) - l) * f;
    } else {
      const f = (base - stepL) / (base - darkest);            // 0 at 600 → 1 at 950
      light = l - (l - Math.min(darkest, l - 1)) * f;
    }
    // very light tints look washed out at full saturation of a grey-ish seed; keep some colour
    const sat = stepL > base ? Math.max(s, 30) : s;
    ramp[step] = hslToHex(h, Math.min(100, sat), Math.max(2, Math.min(98, light)));
  });
  return ramp;
}

export function applyTheme(theme) {
  const t = { ...DEFAULT_THEME, ...theme };
  const root = document.documentElement;

  const primaryRamp = generateRamp(t.primaryColor);
  const secondaryRamp = generateRamp(t.secondaryColor);

  Object.entries(primaryRamp).forEach(([step, hex]) => {
    root.style.setProperty(`--brand-primary-${step}`, hex);
  });
  Object.entries(secondaryRamp).forEach(([step, hex]) => {
    root.style.setProperty(`--brand-secondary-${step}`, hex);
  });

  root.style.setProperty("--brand-primary", primaryRamp[600]);
  root.style.setProperty("--brand-secondary", secondaryRamp[600]);
  root.style.setProperty("--brand-accent", t.accentColor);
  root.style.setProperty("--brand-bg", t.backgroundColor);
  // Section backgrounds: keep the soft primary tint on a white site; with a
  // coloured Background, use that colour so the choice is actually visible.
  const isWhite = /^#?f{3}(f{3})?$/i.test(String(t.backgroundColor).trim());
  root.style.setProperty("--brand-surface", isWhite ? primaryRamp[50] : t.backgroundColor);
}
